import { Fragment, useMemo } from 'react';

import PortalEntry from '@/components/common/PortalEntry';
import { FlattenPortalRequest, RequestWorkflowResponse } from '@/types/sso';
import useLanguage from '@/utils/hooks/useLanguage';
import { useSso } from '@/utils/providers/SSOProvider';

interface BankDetailsProps {
    selectedBankAccount?: FlattenPortalRequest;
    workflowsData?: RequestWorkflowResponse[];
}

const BankDetails = {
    Section1: (props: BankDetailsProps) => {
        const { selectedBankAccount, workflowsData } = props;
        const lang = useLanguage();
        const { currenciesApi } = useSso();

        const { data: currencies } = currenciesApi;

        const swiftCodeLabel = useMemo(() => {
            return selectedBankAccount?.currencyName === 'USD' ? 'SWIFT/BIC/ABA Routing Number' : 'SWIFT/BIC';
        }, [selectedBankAccount]);

        const currencyName = useMemo(() => {
            if (selectedBankAccount?.baseCurrencyName) return selectedBankAccount.baseCurrencyName;
            if (currencies) {
                const currency = currencies.find((c) => c.id === selectedBankAccount?.baseCurrencyId);
                return currency ? currency.digitalAssetName || currency.name : selectedBankAccount?.baseCurrencyId;
            }
        }, [currencies, selectedBankAccount]);

        return (
            <Fragment>
                <fieldset className="space-y-3">
                    <legend className="text-xs text-brand-neutral-500">{lang.portal.generalInformation}</legend>
                    <PortalEntry value={selectedBankAccount?.friendlyName} label={lang.portal.friendlyName} />
                    <PortalEntry value={currencyName} label={lang.lite.currency} />
                </fieldset>
                <fieldset className="space-y-3 mt-3">
                    <legend className="text-xs text-brand-neutral-500">{lang.portal.bankAccountInformation}</legend>
                    <div className="flex flex-col xsm:flex-row gap-4 w-full">
                        <div className="flex-1">
                            <PortalEntry value={selectedBankAccount?.bankName} label={lang.portal.bankName} />
                        </div>
                        <div className="flex-1">
                            <PortalEntry value={selectedBankAccount?.bankAddress} label={lang.portal.bankAddress} />
                        </div>
                    </div>
                </fieldset>
                <fieldset>
                    <PortalEntry value={selectedBankAccount?.swiftCode} label={swiftCodeLabel} />
                </fieldset>
                <fieldset className="space-y-3">
                    <div className="flex flex-col xsm:flex-row gap-4 w-full">
                        <div className="flex-1">
                            <PortalEntry value={selectedBankAccount?.ibanCode} label="IBAN" />
                        </div>
                        <div className="flex-1">
                            <PortalEntry value={selectedBankAccount?.accountNumber} label={lang.portal.accountNumber} />
                        </div>
                    </div>
                </fieldset>
                <fieldset>
                    <PortalEntry value={selectedBankAccount?.description} label={lang.portal.requestDescription} />
                </fieldset>
                {workflowsData && workflowsData.length > 1 && workflowsData[1]?.remark && (
                    <PortalEntry value={workflowsData[1].remark} label={lang.portal.remark} />
                )}
            </Fragment>
        );
    },
    Section2: (props: BankDetailsProps) => {
        const { selectedBankAccount } = props;
        const lang = useLanguage();
        return (
            <Fragment>
                <fieldset className="space-y-3">
                    <legend className="text-xs text-brand-neutral-500">{lang.portal.correspondentInformation}</legend>
                    <div className="flex flex-col xsm:flex-row gap-4 w-full">
                        <div className="flex-1">
                            <PortalEntry
                                value={selectedBankAccount?.correspondentBankName}
                                label={lang.portal.bankName}
                            />
                        </div>
                        <div className="flex-1">
                            <PortalEntry
                                value={selectedBankAccount?.correspondentBankSwiftCode}
                                label={lang.portal.swiftCode}
                            />
                        </div>
                    </div>
                </fieldset>

                <fieldset className="space-y-3 mt-3 w-full">
                    <legend className="text-xs text-brand-neutral-500">{lang.portal.intermediaryInformation}</legend>
                    <div className="flex flex-col xsm:flex-row gap-4 w-full">
                        <div className="flex-1">
                            <PortalEntry
                                value={selectedBankAccount?.intermediaryBankName}
                                label={lang.portal.bankName}
                            />
                        </div>
                        <div className="flex-1">
                            <PortalEntry
                                value={selectedBankAccount?.intermediaryBankSwiftCode}
                                label={lang.portal.swiftCode}
                            />
                        </div>
                    </div>
                </fieldset>
            </Fragment>
        );
    },
    Summary: (props: BankDetailsProps) => {
        const { selectedBankAccount } = props;

        const lang = useLanguage();

        const swiftCodeLabel = useMemo(() => {
            return selectedBankAccount?.currencyName === 'USD'
                ? lang.portal.swiftBICABARoutingNumber
                : lang.portal.swiftBIC;
        }, [lang, selectedBankAccount]);

        return (
            <Fragment>
                <fieldset className="space-y-3 mt-3">
                    <legend className="text-xs text-brand-neutral-500">{lang.portal.bankAccountInformation}</legend>
                    <div className="flex flex-col xsm:flex-row gap-4 w-full">
                        <div className="flex-1">
                            <PortalEntry value={selectedBankAccount?.bankName} label={lang.portal.bankName} />
                        </div>
                        <div className="flex-1">
                            <PortalEntry value={selectedBankAccount?.bankAddress} label={lang.portal.bankAddress} />
                        </div>
                    </div>
                </fieldset>
                <fieldset>
                    <PortalEntry value={selectedBankAccount?.swiftCode} label={swiftCodeLabel} />
                </fieldset>
                <fieldset className="space-y-3">
                    <div className="flex flex-col xsm:flex-row gap-4 w-full">
                        <div className="flex-1">
                            <PortalEntry value={selectedBankAccount?.ibanCode} label="IBAN" />
                        </div>
                        <div className="flex-1">
                            <PortalEntry value={selectedBankAccount?.accountNumber} label={lang.portal.accountNumber} />
                        </div>
                    </div>
                </fieldset>
            </Fragment>
        );
    }
};

export default BankDetails;
